import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '../../state/useSession';
import { useBuildRun } from '../../state/buildRun';
import { useDraft } from '../../state/draft';
import { ApiError } from '../../api/client';
import { planApp } from '../../api/build';
import { scaffoldFor } from '@/webapp/plan/scaffolds';
import { isCollecting, userFeature, type AppPlan } from '@/webapp/plan/planTypes';
import { KITS } from '@/webapp/runtime/kits';
import { Button, Screen } from '../../ui';

type StepId = 'features' | 'look' | 'charts' | 'review';

const TITLES: Record<StepId, string> = {
  features: 'What it does',
  look: 'How it looks',
  charts: 'What it shows',
  review: 'Ready to build',
};

const SUBTITLES: Record<StepId, string> = {
  features: 'Tap to include or leave out. Add anything missing.',
  look: 'Type and colour, chosen for the subject.',
  charts: 'Built from your own data, never invented numbers.',
  review: 'Change anything here — nothing is built until you tap Build.',
};

/**
 * Ported from src/app/(onboarding)/plan.tsx.
 *
 * The plan on screen is a SCAFFOLD rendered synchronously from the idea, so this paints
 * immediately and never shows a spinner over an empty page. The model pass then refines
 * it in place. If that pass fails, times out, or the person is offline, what is already
 * here is a complete, buildable plan — which is why there is no error state.
 */
export default function Plan() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSession();
  const store = useDraft();
  const idea = store.description;

  const [plan, setPlan] = useState<AppPlan>(() => store.plan ?? scaffoldFor(idea));
  const [step, setStep] = useState(0);
  const [suggestion, setSuggestion] = useState<AppPlan | null>(null);
  const [refineNotice, setRefineNotice] = useState<string | null>(null);
  const [newFeature, setNewFeature] = useState('');
  // A user edit must never be overwritten by a refinement that was already in flight.
  const dirty = useRef(false);

  const steps = useMemo<StepId[]>(
    () => (isCollecting(plan) ? ['features', 'look', 'review'] : ['features', 'look', 'charts', 'review']),
    [plan],
  );
  const current = steps[Math.min(step, steps.length - 1)];

  const edit = useCallback((next: (p: AppPlan) => AppPlan) => {
    dirty.current = true;
    setPlan(next);
  }, []);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const { plan: refined } = await planApp(idea);
        if (!live || !refined) return;
        if (dirty.current) setSuggestion(refined);
        else setPlan(refined);
      } catch (err) {
        if (!live) return;
        /*
         * The scaffold on screen is already a complete, buildable plan, so a failed
         * refinement is never fatal and never a redirect. But it is no longer FREE — /plan
         * charges — so silence would leave someone with a quietly worse plan and no idea
         * why. A dismissible line, and Build still works.
         */
        if (err instanceof ApiError && err.kind === 'out_of_credits') {
          setRefineNotice("We couldn't tailor this plan — you're out of credits. You can still build it.");
        } else if (err instanceof ApiError && err.kind === 'unauthorized') {
          setRefineNotice('Sign in and we can tailor this plan to your idea.');
        }
        // Anything else stays silent: the person has a working plan and asked for nothing.
      }
    })();
    return () => {
      live = false;
    };
    // Runs once per idea: the refinement is of THIS idea, and re-running would fight edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea]);

  function build(): void {
    store.setPlan(plan);
    store.setAppName(plan.appName);
        if (!isAuthenticated) {
      store.setPendingIntent('build');
      navigate('/sign-up');
      return;
    }
    void useBuildRun.getState().start(user?.id ?? '');
  }

  const addFeature = (): void => {
    const label = newFeature.trim();
    if (!label) return;
    edit((p) => ({ ...p, features: [...p.features, userFeature(label, p.features)] }));
    setNewFeature('');
  };

  return (
    <Screen title={TITLES[current]}>
      <ol className="plan-progress" aria-label="Progress">
        {steps.map((s, i) => (
          <li key={s} className={i <= step ? 'plan-dot is-done' : 'plan-dot'} aria-current={i === step ? 'step' : undefined}>
            <span className="visually-hidden">{TITLES[s]}</span>
          </li>
        ))}
      </ol>
      <p className="type-body plan-sub">{SUBTITLES[current]}</p>

      {refineNotice && (
        <p role="status" className="type-caption plan-detail plan-notice">
          {refineNotice}{' '}
          <button type="button" className="chip-button" onClick={() => setRefineNotice(null)}>Dismiss</button>
        </p>
      )}

      {suggestion && (
        <div className="plan-suggestion card clay-card" role="status">
          <p className="type-body">We refined this plan while you were editing.</p>
          <Button
            title="Use the refined plan"
            variant="secondary"
            onPress={() => {
              setPlan(suggestion);
              setSuggestion(null);
            }}
          />
          <Button title="Keep mine" variant="ghost" onPress={() => setSuggestion(null)} />
        </div>
      )}

      {current === 'features' && (
        <>
          <ul className="plan-list">
            {plan.features.map((f) => (
              <li key={f.id} className="card clay-card plan-item">
                <label className="plan-check">
                  <input
                    type="checkbox"
                    checked={f.included}
                    onChange={() =>
                      edit((p) => ({
                        ...p,
                        features: p.features.map((x) => (x.id === f.id ? { ...x, included: !x.included } : x)),
                      }))
                    }
                  />
                  <span>
                    <span className="type-heading">{f.label}</span>
                    <span className="type-caption plan-detail">{f.detail}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <div className="plan-add">
            <label className="type-caption field-label" htmlFor="new-feature">Add something missing</label>
            <input
              id="new-feature"
              className="field"
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addFeature();
                }
              }}
            />
            <Button title="Add" variant="secondary" onPress={addFeature} disabled={!newFeature.trim()} />
          </div>
        </>
      )}

      {current === 'look' && (
        <>
          <label className="type-caption field-label" htmlFor="app-name">Name</label>
          <input
            id="app-name"
            className="field"
            value={plan.appName}
            onChange={(e) => edit((p) => ({ ...p, appName: e.target.value }))}
          />
          <label className="type-caption field-label" htmlFor="tagline">Tagline</label>
          <input
            id="tagline"
            className="field"
            value={plan.tagline}
            onChange={(e) => edit((p) => ({ ...p, tagline: e.target.value }))}
          />
          <h2 className="type-micro section-label">Style</h2>
          <div className="kit-grid" role="radiogroup" aria-label="Style">
            {KITS.map((kit) => (
              <button
                key={kit.id}
                type="button"
                role="radio"
                aria-checked={plan.kitId === kit.id}
                className={`card clay-card pressable kit ${plan.kitId === kit.id ? 'is-selected' : ''}`}
                onClick={() => edit((p) => ({ ...p, kitId: kit.id }))}
              >
                <span className="type-heading">{kit.label}</span>
                <span className="type-caption plan-detail">{kit.blurb}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {current === 'charts' && (
        <ul className="plan-list">
          {plan.charts.length === 0 && <li className="type-body">No charts for this one.</li>}
          {plan.charts.map((c) => (
            <li key={c.id} className="card clay-card plan-item">
              <label className="plan-check">
                <input
                  type="checkbox"
                  checked={c.included}
                  onChange={() =>
                    edit((p) => ({
                      ...p,
                      charts: p.charts.map((x) => (x.id === c.id ? { ...x, included: !x.included } : x)),
                    }))
                  }
                />
                <span>
                  <span className="type-heading">{c.label}</span>
                  <span className="type-caption plan-detail">{c.source}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {current === 'review' && (
        <div className="plan-review">
          <h2 className="type-title">{plan.appName}</h2>
          <p className="type-body plan-detail">{plan.tagline}</p>
          <h3 className="type-micro section-label">It will</h3>
          <ul className="plan-review-list">
            {plan.features.filter((f) => f.included).map((f) => (
              <li key={f.id} className="type-body">{f.label}</li>
            ))}
          </ul>
          {plan.constraints.length > 0 && (
            <>
              <h3 className="type-micro section-label">Worth knowing</h3>
              <ul className="plan-review-list">
                {plan.constraints.map((c) => (
                  <li key={c.id} className="type-body">
                    {c.label}
                    {c.workaround && <span className="type-caption plan-detail">{c.workaround}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="plan-actions">
        {step > 0 && <Button title="Back" variant="ghost" onPress={() => setStep((s) => s - 1)} />}
        {current === 'review' ? (
          <Button title="Build it" onPress={build} accessibilityLabel="Build this app" />
        ) : (
          <Button title="Next" onPress={() => setStep((s) => Math.min(s + 1, steps.length - 1))} />
        )}
      </div>
    </Screen>
  );
}
